/**
 * Правила «+» доски расстановки (16.10.2026, владелец) — без запросов и React: грейд римскими, пересечение игр оператора
 * (занят от начала до конца уборки, встык — не конфликт, ночной хвост — по sortTime, спецверсия — по своей длительности),
 * сортировка столбцов «операторы · грейд · игр · график». Хук и запросы — opStats.ts (он же всё это реэкспортирует).
 * 20.10: вынесено, чтобы opStats.test.ts шёл в официальном прогоне веба (node strip-only, без tsx).
 */
import type { BoardGame } from "../../hooks/useOoDay";
import type { AssignMap } from "./scoring";

/** ключ грейда — оператор|квест */
export const gradeKey = (op: string, quest: string) => `${op}|${quest}`;
/** Грейды 1..8 римскими (у бейджа Юниверситета потолок VII — здесь нужен и VIII, ревью 16.10). */
export const gradeLabel = (g: number) => ["I", "II", "III", "IV", "V", "VI", "VII", "VIII"][g - 1] ?? String(g);

export type Span = { duration: number | null; reset: number | null };

const mins = (t: string) => { const [h, m] = t.split(":").map(Number); return (h ?? 0) * 60 + (m ?? 0); };
const hhmm = (m: number) => `${String(Math.floor(m / 60) % 24).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

export interface Clash { id: string; quest: string; time: string; end: string; cleanEnd: string | null; minutes: number }
/** Длительность и уборка игры: у спецверсии — свои, иначе квеста. */
function spanOf(g: BoardGame, versions?: Map<string, Span>): [number, number] {
  const v = g.specialVersionId ? versions?.get(String(g.specialVersionId)) : undefined;
  return [v?.duration ?? (g.duration || 60), v?.reset ?? (g.resetTime || 0)];
}

/** Игры, на которых оператор уже стоит и которые пересекаются с этой (по доске, с несохранённым). */
export function clashesOf(opId: string, game: BoardGame, draft: AssignMap, allGames: BoardGame[], versions?: Map<string, Span>): Clash[] {
  return dayOf(opId, game, draft, allGames, versions).filter((c) => c.minutes > 0);
}

/** 16.10 (владелец): «лонг-пресс по оператору должен показывать, куда его уже поставили на этот день» — все его игры
 *  дня на доске (кроме этой), по времени, у каждой — пересечение с настраиваемой. */
export function dayOf(opId: string, game: BoardGame, draft: AssignMap, allGames: BoardGame[], versions?: Map<string, Span>): Clash[] {
  const [dur, rst] = spanOf(game, versions);
  const s = mins(game.sortTime || game.time), e = s + dur + rst;
  const out: (Clash & { at: number })[] = [];
  for (const [bid, ops] of Object.entries(draft)) {
    if (bid === game.id || !ops.includes(opId)) continue;
    const o = allGames.find((g) => g.id === bid);
    if (!o) continue;
    const [od, or] = spanOf(o, versions);
    const os = mins(o.sortTime || o.time), play = os + od, oe = play + or;
    const minutes = Math.max(0, Math.min(e, oe) - Math.max(s, os));
    out.push({ id: o.id, quest: o.shortName || o.questName, time: o.time, end: hhmm(play), cleanEnd: oe > play ? hhmm(oe) : null, minutes, at: os });
  }
  return out.sort((a, b) => a.at - b.at).map(({ at: _at, ...c }) => c);
}

/** «14:00–15:30 Мотл (уборка до 16:00) — 30 мин» */
export const clashText = (c: Clash) => `${c.time}–${c.end} ${c.quest}${c.cleanEnd ? ` (уборка до ${c.cleanEnd})` : ""} — ${c.minutes} мин`;
/** строка дня: «11:00–12:30 Мотл» или «… — пересекается 30 мин (уборка до 13:00)» */
export const dayText = (c: Clash) =>
  `${c.time}–${c.end} ${c.quest}${c.minutes > 0 ? ` — пересекается ${c.minutes} мин${c.cleanEnd ? ` (уборка до ${c.cleanEnd})` : ""}` : ""}`;

/* ── сортировка «+» (16.10, владелец): «„грейд“, „игр“, „график“ — кликабельные: один клик — по убыванию, второй —
   по возрастанию; столбец „операторы“ — по алфавиту, выбран по умолчанию». Равные — по имени. ── */
export type SortKey = "name" | "grade" | "games" | "sched";
export interface OpSort { key: SortKey; dir: "asc" | "desc" }
export const DEFAULT_SORT: OpSort = { key: "name", dir: "asc" };
/** Клик по заголовку: тот же столбец — другое направление; новый — «операторы» с А, остальные — по убыванию. */
export const nextSort = (cur: OpSort, key: SortKey): OpSort =>
  cur.key === key ? { key, dir: cur.dir === "desc" ? "asc" : "desc" } : { key, dir: key === "name" ? "asc" : "desc" };
export interface SortRow { name: string; grade: number; games: number; can: boolean; clash: boolean }
/** «График» по убыванию: свободен всю игру и без пересечений → свободен, но пересекается → не может → не может и пересекается. */
const schedRank = (r: SortRow) => (r.can ? 2 : 0) + (r.clash ? 0 : 1);
export function sortRows<T extends SortRow>(rows: T[], s: OpSort): T[] {
  const byName = (a: T, b: T) => a.name.localeCompare(b.name, "ru");
  const val = (r: T) => (s.key === "grade" ? r.grade : s.key === "games" ? r.games : schedRank(r));
  return [...rows].sort((a, b) => {
    if (s.key === "name") return s.dir === "asc" ? byName(a, b) : byName(b, a);
    const d = val(a) - val(b);
    return d ? (s.dir === "asc" ? d : -d) : byName(a, b);
  });
}

/** «34 игры» */
export function gamesWord(n: number): string {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return "игра";
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return "игры";
  return "игр";
}
